import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../styles/AdvancedArrayInput.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

// debounce helper
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

/**
 * Editable grid where users can type directly into rows to add or modify items.
 */
const AdvancedArrayInput = ({
  endpoint,
  label,
  name,
  value,
  onChange,
  additionalFields,
  showItemNotFoundWarning = true,
  disabled = false,
}) => {
  const [selectedItems, setSelectedItems] = useState(Array.isArray(value) ? value : []);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState('');

  // Fetch options when query changes
  const fetchOptions = useCallback(
    debounce(async q => {
      if (q) {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}${endpoint}?query=${encodeURIComponent(q)}`
        );
        console.log('fetchOptions response', response.data);
        setOptions(response.data);
      }
    }, 300),
    [endpoint]
  );

  useEffect(() => {
    fetchOptions(query);
  }, [query, fetchOptions]);

  // Propagate external value changes
  useEffect(() => {
    setSelectedItems(Array.isArray(value) ? value : []);
  }, [value]);

  const handleNameChange = (index, nameValue) => {
    if (disabled) return;
    setQuery(nameValue);
    const matched = options.find(opt => opt.item_name === nameValue);
    
    if (index < selectedItems.length) {
      // editing existing item
      const newItems = selectedItems.map((item, i) =>
        i === index ? {
          ...matched, // Copy matched item properties first
          name: nameValue, // Override with entered name
          // Keep existing additional field values
          ...additionalFields.reduce((acc, f) => ({ 
            ...acc, 
            [f.name]: item[f.name] || '' 
          }), {})
        } : item
      );
      setSelectedItems(newItems);
      onChange(name, newItems);
    } else {
      // new row entry
      if (!nameValue.trim()) return;
      if (!matched) {
        console.warn(`Item "${nameValue}" not found in options.`);
      }
      const newItem = {
        ...matched,
        name: nameValue,
        ...additionalFields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})
      };
      const newItems = [...selectedItems, newItem];
      setSelectedItems(newItems);
      onChange(name, newItems);
    }
  };

  const handleFieldChange = (index, fieldName, fieldValue) => {
    if (disabled) return;
    const newItems = selectedItems.map((item, i) =>
      i === index ? { ...item, [fieldName]: fieldValue } : item
    );
    setSelectedItems(newItems);
    onChange(name, newItems);
  };

  const handleRemove = index => {
    if (disabled) return;
    const newItems = selectedItems.filter((_, i) => i !== index);
    setSelectedItems(newItems);
    onChange(name, newItems);
  };

  const rows = disabled ? selectedItems : [...selectedItems, { isNew: true }];

  return (
    <div className="advanced-array-input">
      <table className="advanced-array-table">
        <thead>
          <tr>
            <th className="nameCol">Name</th>
            {additionalFields.map(f => (
              <th key={f.name} style={{ width: f.width }}>{f.label}</th>
            ))}
            <th className="actionCol">A</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item, idx) => (
            <tr key={idx}>
              <td className="nameCol">
                <input
                  type="text"
                  value={item.name || ''}
                  onChange={e => handleNameChange(idx, e.target.value)}
                  list={disabled ? undefined : `${name}-options`}
                  placeholder={disabled ? '' : 'Type to search or add...'}
                  disabled={disabled}
                  readOnly={disabled}
                />
                {!disabled && (
                  <datalist id={`${name}-options`}>
                    {options.map((opt, i) => (
                      <option key={i} value={opt.item_name} />
                    ))}
                  </datalist>
                )}
                {showItemNotFoundWarning && idx < selectedItems.length && !item.item_id && (
                  <div className="missing-item-id-warning">* Not found</div>
                )}
              </td>
              {additionalFields.map(f => (
                <td key={f.name}>
                  {f.type === 'dropdown' ? (
                    <select
                      value={item[f.name] || ''}
                      onChange={e => handleFieldChange(idx, f.name, e.target.value)}
                      disabled={disabled}
                    >
                      <option value="">Select...</option>
                      {f.options.map((opt, oi) => (
                        <option key={oi} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type}
                      value={item[f.name] || ''}
                      onChange={e => handleFieldChange(idx, f.name, e.target.value)}
                      disabled={disabled}
                      readOnly={disabled}
                    />
                  )}
                </td>
              ))}
              <td>
                {!disabled && idx < selectedItems.length ? (
                  <button type="button" onClick={() => handleRemove(idx)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdvancedArrayInput;
